/**
 * O resultado de um requisito da matriz pública pelo painel (E211.F3, owner, 01/11; ADR-0146,
 * adendo E211.F3): o perito escolhe o resultado (o vocabulário da matriz, sem o não exigível) com
 * fundamento; a matriz imprime o resultado dele com a nota numerada e a contagem dos requisitos
 * acompanha. A conclusão da formalização não muda por aqui — o texto do painel diz isso; o
 * requisito com revisão formal (a face, a autorização) segue pela aba Pendências e não chega aqui.
 *
 * O formulário abre pelo resultado impresso na linha; o Salvar fica ligado e diz o que falta.
 */
import { useState } from 'react';

import { Field, Select } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  RESULTADOS_DO_REQUISITO,
  requisitoImpresso,
  vigenteEntre,
  type ResultadoDoRequisito,
} from '@/lib/revisaoDoDado';

import { Fundamento, faltaNoFundamento } from './Fundamento';
import { BotaoSalvar, Falta, useGravacao } from './Gravacao';
import { gravarRevisao } from './gravarRevisao';
import type { PropsDoConteudo } from './tipos';

const t = ptBR.painelDoDado.requisito;
const falta = ptBR.painelDoDado.falta;

export function RequisitoNoPainel(props: PropsDoConteudo) {
  const { pedido, acoes, onMudou } = props;
  const [resultado, setResultado] = useState<ResultadoDoRequisito | ''>(() =>
    requisitoImpresso(pedido.origem.valor),
  );
  const [fundamento, setFundamento] = useState('');
  const [oQueFalta, setOQueFalta] = useState<string | null>(null);
  const gravacao = useGravacao(
    () =>
      gravarRevisao(props, {
        alvo: 'linha',
        chave: pedido.origem.linha ?? '',
        valor: resultado,
        fundamento: fundamento.trim(),
      }),
    () => {
      acoes.salvo();
      onMudou();
    },
  );
  function salvar(): void {
    const pendente = resultado === '' ? falta.requisito : faltaNoFundamento(fundamento);
    setOQueFalta(pendente);
    if (pendente === null) void gravacao.salvar();
  }
  return (
    <div className="space-y-sc-3" data-testid="painel-requisito">
      <p className="text-body-sm text-fg-muted">{t.texto}</p>
      <Field label={t.rotulo} htmlFor="painel-requisito-resultado">
        <Select
          id="painel-requisito-resultado"
          value={resultado}
          onChange={(e) => setResultado(vigenteEntre(RESULTADOS_DO_REQUISITO, e.target.value))}
          data-testid="painel-requisito-resultado"
        >
          <option value="" disabled>
            {t.escolha}
          </option>
          {RESULTADOS_DO_REQUISITO.map((r) => (
            <option key={r} value={r}>
              {t.resultados[r]}
            </option>
          ))}
        </Select>
      </Field>
      <Fundamento id="painel-requisito-fundamento" valor={fundamento} onChange={setFundamento} />
      <Falta falta={oQueFalta} estado={gravacao.estado} testId="painel-requisito-falta" />
      <BotaoSalvar
        estado={gravacao.estado}
        recusa={gravacao.recusa}
        onSalvar={salvar}
        testId="painel-requisito-salvar"
      />
    </div>
  );
}
